import { useEffect, useMemo, useState } from 'react';
import BookmarkAdvancedFilters from '../../components/bookmark/BookmarkAdvancedFilters';
import CompactAddBookmarkDialog from '../../components/bookmark/CompactAddBookmarkDialog';
import MobileBookmarkList from '../../components/bookmark/MobileBookmarkList';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useBookmarks } from '../../hooks/useBookmarks';
import {
  getTimeRangeStartMs,
  type TimeRangePreset,
} from '../../utilities/bookmark/timeRangeFilter';
import { IBookmarkWithFolder } from '../../utilities/types/BookmarkExtensions';

export default function DashboardScreen() {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'all' | 'favorite'>('all');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [activeFolderId, setActiveFolderId] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<TimeRangePreset>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [layout, setLayout] = useState<'single' | 'multi-column'>('multi-column');

  const {
    bookmarks,
    loading,
    error,
    updateBookmark,
    refreshBookmarks,
    deleteBookmark,
  } = useBookmarks();

  useEffect(() => {
    refreshBookmarks();
  }, []);

  /** Sidebar quick filters */
  const handleFilterChange = (filter: string) => {
    if (filter === 'all') {
      setActiveFilter('all');
      setActiveCategory(null);
      setActiveFolderId(null);
      setTimeRange('all');
      setSearchQuery('');
      return;
    }
    if (filter === 'recent') {
      setActiveFilter('all');
      setTimeRange('7d');
      return;
    }
    if (filter === 'favorite') {
      setActiveFilter('favorite');
      return;
    }
  };

  /** Toggle same category off */
  const handleCategorySelect = (categoryId: string) => {
    setActiveCategory((prev) => (prev === categoryId ? null : categoryId));
  };

  const handleFolderSelect = (folderId: string | null) => {
    setActiveFolderId(folderId);
  };

  const handleBookmarkAdded = async () => {
    setShowAddDialog(false);
    await refreshBookmarks();
  };

  const filteredBookmarks = useMemo(() => {
    let filtered = [...bookmarks] as IBookmarkWithFolder[];
    const timeCutoff = getTimeRangeStartMs(timeRange);

    if (activeFolderId !== null) {
      filtered = filtered.filter((b) => b.folderId === activeFolderId);
    }

    if (activeFilter === 'favorite') {
      filtered = filtered.filter((b) => b.is_starred === 'true');
    }

    if (activeCategory) {
      filtered = filtered.filter((b) => b.categories?.includes(activeCategory));
    }

    if (timeCutoff !== null) {
      filtered = filtered.filter((b) => (b.addedOn ?? 0) >= timeCutoff);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (b) =>
          b.title?.toLowerCase().includes(query) ||
          b.description?.toLowerCase().includes(query) ||
          b.url?.toLowerCase().includes(query)
      );
    }

    filtered.sort((a, b) => (b.addedOn || 0) - (a.addedOn || 0));

    return filtered;
  }, [bookmarks, activeFilter, activeCategory, activeFolderId, timeRange, searchQuery]);

  if (error) {
    return <div>Error: {error}</div>;
  }

  const LayoutToggle = () => (
    <div
      className="flex w-full max-w-full items-stretch gap-1 rounded-[14px] border p-1 min-h-[40px] font-sans"
      style={{
        backgroundColor: 'var(--sfl-surface)',
        borderColor: 'var(--sfl-border2)',
      }}
    >
      <button
        type="button"
        title="Single column"
        onClick={() => setLayout('single')}
        className={`flex-1 flex items-center justify-center py-2 text-sm rounded-[10px] transition-colors ${layout === 'single'
          ? 'bg-[var(--sfl-accent-light)] text-[var(--sfl-accent)]'
          : 'text-[var(--sfl-text2)] hover:text-[var(--sfl-text)]'
          }`}
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
      </button>
      <button
        type="button"
        title="Multi-column grid"
        onClick={() => setLayout('multi-column')}
        className={`flex-1 flex items-center justify-center py-2 text-sm rounded-[10px] transition-colors ${layout === 'multi-column'
          ? 'bg-[var(--sfl-accent-light)] text-[var(--sfl-accent)]'
          : 'text-[var(--sfl-text2)] hover:text-[var(--sfl-text)]'
          }`}
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <rect x="3" y="3" width="7" strokeWidth={2} height="7" />
          <rect x="14" y="3" width="7" strokeWidth={2} height="7" />
          <rect x="14" y="14" width="7" strokeWidth={2} height="7" />
          <rect x="3" y="14" width="7" strokeWidth={2} height="7" />
        </svg>
      </button>
    </div>
  );

  const advancedFilters = (
    <BookmarkAdvancedFilters
      variant="sidebar"
      bookmarks={bookmarks}
      selectedFolderId={activeFolderId}
      onFolderChange={handleFolderSelect}
      selectedCategoryId={activeCategory}
      onCategoryChange={setActiveCategory}
      timeRange={timeRange}
      onTimeRangeChange={setTimeRange}
    />
  );

  return (
    <DashboardLayout
      onSearchChange={setSearchQuery}
      searchQuery={searchQuery}
      onAddBookmark={() => setShowAddDialog(true)}
      onFilterChange={handleFilterChange}
      onCategorySelect={handleCategorySelect}
      activeCategoryId={activeCategory}
      sidebarAdvancedFilters={advancedFilters}
      sidebarLayoutToggle={<LayoutToggle />}
    >
      <MobileBookmarkList
        bookmarks={filteredBookmarks}
        loading={loading}
        onUpdate={updateBookmark}
        onDelete={deleteBookmark}
        onRefresh={refreshBookmarks}
        layout={layout}
      />
      <CompactAddBookmarkDialog
        isOpen={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onBookmarkAdded={handleBookmarkAdded}
      />
    </DashboardLayout>
  );
}
